/**
 * Settings definition for the Company PTO Buckets dashboard.
 *
 * Renders a table of CompanyPTOBucket records with inline editing for
 * name, bucket type (Cost Code / Absence Type), and absence type assignment.
 */
import { companyAbsenceTypeColDefCreator, stringColDefCreator } from "./standard-col-def-creators"
import { alertCellClassRule } from "../../../common/ag-grid-utils"

/** Human-readable labels for the PTOBucketType enum values */
const BUCKET_TYPE_LABELS: Record<string, string> = {
    COST_CODE: "Cost Code",
    ABSENCE_TYPE: "Absence Type",
}

/**
 * Build CustomTable settings for the Company PTO Buckets dashboard.
 *
 * Returns:
 *     Record<string, unknown>: Table settings object for CustomTable.
 */
export const getCompanyPTOBucketsSettings = (): Record<string, unknown> => {
    return {
        tableName: "PTO Buckets",
        navId: "companyPTOBuckets",
        resources: ["companyPTOBuckets"],
        // relatedNames is a two-level map: the outer key is the *row* resource,
        // the inner key is the resource to load referenceables for.
        // (Without the outer `companyPTOBuckets` key, _getReferenceableIds bails
        // before ever requesting absence-type data, which is why the Absence
        // Type cell renders empty even though the row carries an ID.)
        relatedNames: {
            companyPTOBuckets: {
                companyAbsenceTypes: ["/absence_type"],
            },
        },
        filters: [],
        colDefs: [
            stringColDefCreator({
                headerName: "Name*",
                field: "/name",
                required: true,
                cellClassRules: { "alert-cell": alertCellClassRule },
                parentContainerId: "side-rail-wrapper",
            }),
            {
                ...stringColDefCreator({
                    headerName: "Bucket Type*",
                    field: "/bucket_type",
                    required: true,
                    enum: Object.values(BUCKET_TYPE_LABELS),
                    minWidth: 150,
                    width: 150,
                }),
                valueFormatter: (params: { value: string }) => BUCKET_TYPE_LABELS[params.value] || params.value || "",
                valueSetter: (params: { data: Record<string, unknown>; newValue: string }) => {
                    params.data.bucket_type = Object.keys(BUCKET_TYPE_LABELS).find(key => BUCKET_TYPE_LABELS[key as keyof typeof BUCKET_TYPE_LABELS] === params.newValue)
                    return params.data.bucket_type
                },
            },
            {
                ...companyAbsenceTypeColDefCreator({
                    headerName: "Absence Type",
                    field: "/absence_type",
                    required: false,
                    editable: (params: { data: Record<string, unknown> }) => {
                        return params.data?.bucket_type === "ABSENCE_TYPE"
                    },
                }),
            },
        ],
        gridSettings: {
            rowHeight: 40,
            defaultColDef: {
                editable: true,
            },
        },
        otherSettings: {
            pageTitle: "PTO Buckets",
            subcontent: "Manage your company's PTO buckets. Each bucket tracks PTO either through a " + "project cost code or an absence type.",
            rowLevelErrorDisplay: true,
            enableSearchBar: true,
            buttons: {
                table: [
                    {
                        label: "Add Row",
                        icon: "add",
                        action: "addNewRow",
                    },
                ],
                row: [],
            },
        },
    }
}
